import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import {
	ClassNameProps,
	Column,
	compareObjects,
	compareStringsAsc,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
	Section,
	sort,
	StyleProps,
	ViewableObserver,
} from "@hex-insights/core";
import { FormState } from "@hex-insights/forms";
import { InternalLink } from "@hex-insights/router";
import { Icon } from "../../../../Components/Icon";
import { useHubPageInfo } from "../../../../Contexts";
import {
	AcademicSubjectOrderField,
	CourseSectionFilterInput,
	mergeFilterInputs,
	OrderDirection,
	useAcademicSubjectNamesQuery,
	useCourseSectionListQuery,
} from "../../../../Utilities";
import {
	CourseSectionFilterField,
	CourseSectionFilterForm,
	CourseSectionFilterFormState,
	CourseSectionFilterFormUtils,
	CourseSectionFilterFormValues,
} from "../../../Filter";
import { AcademicSubjectLink, CourseSectionLink } from "../../../Links";
import { CourseSectionSimpleCard, CourseSectionSimpleCardProps } from "../Card";

export type ControlledCourseSectionsListModalProps = Omit<
	CourseSectionsListModalProps,
	"isLoading" | "courseSections"
> & {
	filters?: CourseSectionFilterInput;
};

export function ControlledCourseSectionsListModal({ filters, ...props }: ControlledCourseSectionsListModalProps) {
	const filterFormState = CourseSectionFilterFormState.useFilterFormState();
	const filterInput = IndexForms.useFilterInput(
		filterFormState.formValues,
		CourseSectionFilterFormUtils.toFilterInputs,
	);

	const { loading, data } = useCourseSectionListQuery({
		variables: {
			filters: mergeFilterInputs(filterInput, filters),
		},
	});
	const courseSections = React.useMemo(() => data?.courseSectionConnection.edges.map((e) => e.node), [data]);

	return (
		<CourseSectionsListModal
			isLoading={loading}
			courseSections={courseSections}
			filterFormState={filterFormState}
			{...props}
		/>
	);
}

const pageSize = 25;

export type CourseSectionsListModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	heading?: JSX.Element | string;
	isLoading?: boolean;
	courseSections: CourseSectionSimpleCardProps["courseSection"][] | null | undefined;
	filterFormState?: FormState<CourseSectionFilterFormValues.FilterFormValues>;
	groupBy?: "academicSubject";
} & Partial<ClassNameProps & StyleProps>;

export function CourseSectionsListModal({
	heading = "Course Sections",
	courseSections,
	filterFormState,
	groupBy,
	ifRef,
	onClose,
	className,
	style,
}: CourseSectionsListModalProps) {
	const orderedCourseSections = React.useMemo(
		() =>
			sort(
				courseSections ?? [],
				compareObjects((e) => e.course.academicSubject.name, compareStringsAsc()),
			),
		[courseSections],
	);

	const [numShown, setNumShown] = React.useState(pageSize);
	const showMore = React.useCallback(() => setNumShown((numShown) => numShown + pageSize), []);

	const slicedCourseSections = React.useMemo(
		() => orderedCourseSections.slice(0, numShown),
		[orderedCourseSections, numShown],
	);

	const indexPage = useHubPageInfo("CourseSection", "index");

	return (
		<Modal ifRef={ifRef} onClose={onClose} size="large" className={className} style={style}>
			<Modal.Header>
				<Row justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<Heading.H2 noMargin>{heading}</Heading.H2>

					{!!indexPage && (
						<InternalLink
							to={indexPage.to}
							className="link--primary link--no-text-decoration"
							style={{ fontSize: "0.875rem", marginLeft: "1rem", fontWeight: "bold" }}
						>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
								Go to Index <Icon.ArrowRight size={12} />
							</Row>
						</InternalLink>
					)}
				</Row>
			</Modal.Header>
			<Modal.Body>
				{!!filterFormState && (
					<Row justify="spaced-start" align="center" horizontalSpacing="1rem" style={{ marginBottom: "1.5rem" }}>
						<CourseSectionFilterForm.Search formState={filterFormState} />
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<CourseSectionFilterField.AcademicSubjectIDInTooltipButton formState={filterFormState} />
							<CourseSectionFilterField.ClassYearIDsInTooltipButton formState={filterFormState} />
						</Row>
					</Row>
				)}
				<Conditional>
					<If condition={groupBy === "academicSubject"}>
						<CourseSectionListGroupByAcademicSubjectContent courseSections={slicedCourseSections} />
					</If>
					<Else>
						<CourseSectionsListFlatContent courseSections={slicedCourseSections} />
					</Else>
				</Conditional>
				<If condition={!!courseSections && numShown < courseSections.length}>
					<ViewableObserver onEnterView={showMore} rootMargin="0px 0px 200px" />
				</If>
			</Modal.Body>
		</Modal>
	);
}

type CourseSectionListGroupByAcademicSubjectContentProps = {
	courseSections: CourseSectionsListModalProps["courseSections"];
};

function CourseSectionListGroupByAcademicSubjectContent({
	courseSections,
}: CourseSectionListGroupByAcademicSubjectContentProps) {
	const { data: academicSubjectData } = useAcademicSubjectNamesQuery({
		variables: { order: { field: AcademicSubjectOrderField.Name, direction: OrderDirection.Asc } },
	});

	const courseSectionsAcademicSubject = React.useMemo(() => {
		const courseSectionsAcademicSubject: Record<
			number,
			Exclude<CourseSectionsListModalProps["courseSections"], null | undefined>
		> = {};

		if (!courseSections) {
			return courseSectionsAcademicSubject;
		}

		for (let i = 0; i < courseSections.length; i++) {
			const courseSection = courseSections[i];
			const key = courseSection.course.academicSubject.id;
			if (!courseSectionsAcademicSubject[key]) {
				courseSectionsAcademicSubject[key] = [];
			}
			courseSectionsAcademicSubject[key].push(courseSection);
		}

		return courseSectionsAcademicSubject;
	}, [courseSections]);

	return (
		<Column justify="spaced-start">
			{academicSubjectData?.academicSubjectConnection.edges.map(({ node: academicSubject }) => {
				const courseSections = courseSectionsAcademicSubject[academicSubject.id] ?? [];

				if (courseSections.length === 0) {
					return null;
				}

				return (
					<Section key={academicSubject.id}>
						<Section.Header>
							<Heading.H3 noMargin>
								<AcademicSubjectLink instance={academicSubject} className="link--no-text-decoration" />
							</Heading.H3>
						</Section.Header>
						<Section.Body>
							<CourseSectionsListFlatContent courseSections={courseSections} />
						</Section.Body>
					</Section>
				);
			})}
		</Column>
	);
}

const responsiveTracks = {
	0: 1,
	700: 2,
	1000: 3,
};

type CourseSectionsListFlatContentProps = {
	courseSections: CourseSectionsListModalProps["courseSections"];
};

function CourseSectionsListFlatContent({ courseSections }: CourseSectionsListFlatContentProps) {
	return (
		<Grid responsiveTracks={responsiveTracks} gap="1rem">
			{courseSections?.map((courseSection) => (
				<CourseSectionLink key={courseSection.id} instance={courseSection} className="link--no-text-decoration">
					<CourseSectionSimpleCard courseSection={courseSection} style={{ height: "100%" }} />
				</CourseSectionLink>
			))}
		</Grid>
	);
}
